import { type UtcMilliseconds } from '../general';

///
/// General types:
/// Types that describe more global aspects of
/// NFTs, Blockchains, etc.

export enum EthTokenStardard {
    Erc721 = 'Erc-721',
    Erc1155 = 'Erc-1155',
}

export enum EthMintFrom {
    Null = 'NullAddress',
}

///
/// User / Wallet / Analytics types:
/// Types that describe a User (Yat, Twitter, etc),
/// along with their Wallet(s), as well as any
/// meaningful analytics.

export interface FollowersData {
    count: number;
    // Is this data point correct?
    dailyGrowth: number;
}

export interface WalletAnalytics {
    balance: number;
    dailyGrowth: number;
    profits: number;
    losses: number;
}

export interface YatUserTwitter {
    handle: string;
    followers: FollowersData;
}

export interface YatUserYat {
    yats: string[];
    followers: FollowersData;
    primary?: string;
    // If `yats` stores something more than just a `string`,
    // offer a getter to quickly retrieve the `primary` Yat data.
}

export interface YatUserWallet {
    id: string;
    currency: FloorGameCrypto;
    analytics?: WalletAnalytics;
}

export interface YatUserMultiWallet {
    primary: string;
    wallets: YatUserWallet[];
    // Offer a getter to quickly retrieve the `primary` wallet data.
}

export interface YatUser {
    id: string;
    yat: YatUserYat;
    profileUrl: string;
    wallet: YatUserMultiWallet;
    twitter?: YatUserTwitter;
}

///
/// Blockchain types:
/// Types that describe the Blockchains, Currencies,
/// and Marketplaces supported within Floor Game.

export enum FloorGameBlockchain {
    Ethereum = 'Ethereum',
}

export enum FloorGameCrypto {
    Ethereum = 'Ether',
}

export enum FloorGameCryptoCode {
    Ethereum = 'ETH',
}

export enum FloorGameMarketplace {
    OpenSea = 'OpenSea',
}

///
/// Tier / Price / Death types:
/// Types that describe the "game mechanics" of the
/// Floor Game. In the future, we might want to
/// determine tiers dynamically based on crypto value.

export enum FloorGameTierLabel {
    Tier1 = 'tier1',
    Tier2 = 'tier2',
    Tier3 = 'tier3',
    Tier4 = 'tier4',
    Tier5 = 'tier5',
}

export enum FloorGameEthTierFloor {
    Tier1 = 0,
    Tier2 = 0.02,
    Tier3 = 0.2,
    Tier4 = 2,
    Tier5 = 1000,
}

export enum FloorGameActivity {
    // Red: Many more new listings than sales.
    LowDemand = 'lowDemand',
    // Yellow: No new listing or sales (activity has settled).
    Quiet = 'quiet',
    // Green: Many more sales than new listings.
    HighDemand = 'highDemand',
    // Green + Fire: Many sales within a short time window.
    OnFire = 'onFire',
}

export enum FloorGameEvent {
    Acquired = 'acquired',
    Sold = 'sold',
    Burned = 'burned',
    TierUp = 'tierUp',
    TierDown = 'tierDown',
    Revived = 'revived',
    Dying = 'dying',
    Dead = 'dead',
}

export enum FloorGameIncomingEvent {
    // What do these events relate to?
    Sale = 'sale',
    Sent = 'sent',
    Received = 'received',
}

///
/// NFT dependant types:
/// Types that describe details relevant to
/// constructing an NFT interface.

export enum NftStatus {
    Listed = 'listed',
    Unlisted = 'unlisted',
    // Do we retain `Burned` NFTs?
    // Burned = 'burned',
    // Is there any good reason to consider
    // a `transer` state?
    // Transferring = "transferring",
}

export interface NftMarket {
    marketplace: FloorGameMarketplace;
    buyUrl?: string;
}

export interface NftCreator {
    name: string;
    thumbnail?: string;
    url?: string;
}

export interface NftContract {
    address: string;
    blockchain: FloorGameBlockchain;
    standard: EthTokenStardard;
    creator: NftCreator;
    name: string;
}

export interface NftCollectionAnalytics {
    // `volume` and `floor price` need to be clarified.
    volume: number;
    floorPrice: number;
    avgHolding: Date;
}

export interface NftCollection {
    // TODO: Does a `collection` require a unique `id`?
    // Or is there always a unique `contract` associated with
    // each `collection` (and thus we can rely on a unique `address`)?
    name: string;
    thumbnail?: string;
    url?: string;
    slug?: string;
    analytics?: NftCollectionAnalytics;
}

export interface NftAsset {
    // Props map to `ThumbnailProps`.
    title?: string;
    videoUrl?: string;
    imageUrl?: string;
}

export interface NftValue {
    currency: FloorGameCrypto;
    price: number;
    // TODO: Should be `priceUsd`.
    usdPrice?: number;
    // The assumption here is we poll `price / usdPrice`
    // at an interval to get the latest market / conversion
    // value. Perhaps we don't actually need this prop.
    lastUpdated?: UtcMilliseconds;
}

export interface NftValueCollected extends NftValue {
    estimation?: boolean;
    // `gains` should be derived from the "complex timeline".
    // Assumption is we look-up what this `owner` paid to acquire the NFT,
    // and we compare that to the `floorPrice / estimation / something` to
    // determine what the profit / loss expectation is.
    gains?: number;
}

export interface NftValueCommon {
    // TODO: This `interface` is mostly just a proxy
    // for `CurrencyDisplayProps`. We should
    // reconsider whether we want to keep this.
    currency?: NftValue['currency'];
    price?: NftValue['price'];
    localConversion?: string;
}

///
/// NFT types:
/// Types that describe an individual NFT,
/// to be rendered within the Floor Game.

export interface NftCommon {
    id: string;
    name: string;
    contract: NftContract;
    collection: NftCollection;
    asset: NftAsset;
    value: NftValue;
    // Can there be multiple owners?
    // See `top-ownerships` from OpenSea.
    owner?: string | YatUser;
    status?: NftStatus;
    market?: NftMarket;
}

export interface YatOwnedNft extends Omit<NftCommon, 'owner'> {
    // Can there be multiple "Yat user owners"?
    owner: YatUser;
}

export type NftCommonLibrary = NftCommon[];
export type YatOwnedNftLibrary = YatOwnedNft[];

///
/// NFT event types:
/// Types that describe events that occur
/// on an individual NFT or collection.

export enum NftTransaction {
    Burn = 'burn',
    Mint = 'mint',
    List = 'list',
    Unlist = 'unlist',
    Sale = 'sale',
    // Offer = "offer",
    // Transfer = "transfer",
}

export interface NftTimelineEventAsset {
    id: NftCommon['id'];
    name: NftCommon['name'];
    thumbnail: NftAsset['imageUrl'];
}

export interface NftTimelineEvent {
    // TODO: Consider naming this prop `transaction`.
    type: NftTransaction;
    timestamp: UtcMilliseconds;
    asset: NftTimelineEventAsset;
    value: NftValue;
    // Can there be multiple owners / recipients?
    // See `top-ownerships` from OpenSea.
    owner?: string;
    recipient?: string;
}

export type NftTimeline = NftTimelineEvent[];

export enum EventAnimation {
    LevelDown = 'LevelDown',
    LevelUp = 'LevelUp',
}
